import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Tooltip,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Stack,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Box,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import DateRangeFilter from "../components/common/DateRangeFilter";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";
import CreateQuotationModal from "../components/quotations/CreateQuotationModal";

import useDebounce from "../hooks/useDebounce";
import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getQuotations,
  updateQuotationStatus,
  deleteQuotation,
  resetQuoteServerStatus,
} from "../redux/actions/quotationActions";
import StatusChip from "../components/common/StatusChip";
import { QuotationSummaryDto } from "../shared/dtos/quotation/QuotationSummaryDto";
import { DocumentStatus } from "../shared/enums/DocumentStatus";
import { formatPlainNumber } from "../utils/appUtils";

const STATUS_OPTIONS: DocumentStatus[] = [
  DocumentStatus.DRAFT,
  DocumentStatus.SENT,
  DocumentStatus.ACCEPTED,
  DocumentStatus.REJECTED,
  DocumentStatus.EXPIRED,
  DocumentStatus.VOIDED,
  DocumentStatus.CONVERTED,
];

const QuotationsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { navigateToTab } = useWorkspaceNavigation();

  // --- Redux State ---
  const {
    items: quotations,
    pagination,
    loadingFetchAll: loadingQuotations,
    errorFetchAll: errorLoadingQuotations,
    loadingDelete: loadingDeleteQuotation,
    serverStatus,
    errorCreate: errorCreateQuote,
  } = useAppSelector((state) => state.quotation);

  const today = new Date().toISOString().split("T")[0];

  // --- Local Filters & Pagination ---
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);
  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);
  const [selectedStatuses, setSelectedStatuses] = useState<DocumentStatus[]>(
    [],
  );
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  // --- Modals & Menus ---
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
  const [quoteForOperation, setQuoteForOperation] =
    useState<QuotationSummaryDto | null>(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuQuoteId, setMenuQuoteId] = useState<string | null>(null);

  // --- Fetch Logic ---
  const fetchList = useCallback(() => {
    dispatch(
      getQuotations({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
        startDate,
        endDate,
        statuses: selectedStatuses,
      }),
    );
  }, [
    dispatch,
    pageConfig,
    debouncedSearch,
    startDate,
    endDate,
    selectedStatuses,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch, startDate, endDate, selectedStatuses]);

  // --- Handlers ---
  const handleOpenDelete = (quote: QuotationSummaryDto): void => {
    setQuoteForOperation(quote);
    setIsDeleteOpen(true);
  };

  const handleCloseDelete = useCallback(() => {
    if (!loadingDeleteQuotation) {
      setIsDeleteOpen(false);
      setQuoteForOperation(null);
    }
  }, [loadingDeleteQuotation]);

  const handleDeleteConfirm = (): void => {
    if (quoteForOperation) {
      dispatch(deleteQuotation(quoteForOperation.id));
    }
  };

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "delete" && status === 200) {
      handleCloseDelete();
      dispatch(resetQuoteServerStatus());
    }
    if (operation === "create" && status === 201) {
      setIsCreateOpen(false);
      dispatch(resetQuoteServerStatus());
    }
  }, [serverStatus, dispatch, handleCloseDelete]);

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    id: string,
  ): void => {
    setAnchorEl(event.currentTarget);
    setMenuQuoteId(id);
  };
  const handleMenuClose = (): void => {
    setAnchorEl(null);
    setMenuQuoteId(null);
  };

  const handleStatusChange = (newStatus: DocumentStatus): void => {
    if (menuQuoteId) {
      dispatch(updateQuotationStatus(menuQuoteId, newStatus));
    }
    handleMenuClose();
  };

  // --- Table Columns ---
  const columns: TableColumn<QuotationSummaryDto>[] = useMemo(
    () => [
      {
        id: "quoteNumber",
        label: "QUOTE #",
        minWidth: 110,
        format: (_, row) => (
          <Box component="b" sx={{ color: "#c77dff" }}>
            {row.quoteNumber}
          </Box>
        ),
      },
      {
        id: "issueDate",
        label: "Issue Date",
        minWidth: 100,
        format: (_, row) => new Date(row.issueDate).toLocaleDateString(),
      },
      {
        id: "expiryDate",
        label: "Valid Until",
        minWidth: 100,
        format: (_, row) => new Date(row.expiryDate).toLocaleDateString(),
      },
      { id: "customerName", label: "Customer", minWidth: 150 },
      {
        id: "status",
        label: "Status",
        minWidth: 110,
        format: (_, row) => <StatusChip status={row.status} />,
      },
      { id: "currencyCode", label: "CUR", minWidth: 60, align: "center" },
      {
        id: "total",
        label: "Total Amount",
        minWidth: 120,
        align: "right",
        format: (_, row) => (
          <Typography variant="body2" sx={{ fontWeight: "bold" }}>
            {formatPlainNumber(row.total)}
          </Typography>
        ),
      },
      {
        id: "subtotal",
        label: "Subtotal",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.subtotal),
      },
      {
        id: "totalDiscount",
        label: "Discount",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.totalDiscount),
      },
      {
        id: "totalTaxableAmount",
        label: "Taxable",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.totalTaxableAmount),
      },
      {
        id: "totalTax",
        label: "Tax",
        minWidth: 100,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.totalTax),
      },
      {
        id: "exchangeRate",
        label: "Rate",
        minWidth: 80,
        defaultHidden: true,
        align: "right",
        format: (_, row) => formatPlainNumber(row.exchangeRate),
      },
    ],
    [],
  );

  const actionStatuses: DocumentStatus[] = [
    DocumentStatus.DRAFT,
    DocumentStatus.SENT,
    DocumentStatus.ACCEPTED,
    DocumentStatus.REJECTED,
    DocumentStatus.VOIDED,
  ];

  return (
    <PageContainer
      title="Quotations"
      subtitle="Manage estimates, update statuses, and convert approved quotes to invoices."
      actions={
        <AppButton
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={() => setIsCreateOpen(true)}
        >
          New Quotation
        </AppButton>
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search Quote # or Customer..."
      >
        <DateRangeFilter
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Statuses</InputLabel>
          <Select
            multiple
            value={selectedStatuses}
            onChange={(e) =>
              setSelectedStatuses(e.target.value as DocumentStatus[])
            }
            input={<OutlinedInput label="Statuses" />}
            renderValue={(selected) => selected.join(", ")}
          >
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                <Checkbox checked={selectedStatuses.indexOf(status) > -1} />
                <ListItemText primary={status} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </PageToolbar>

      <StandardTable
        columns={columns}
        data={quotations}
        loading={loadingQuotations}
        error={errorLoadingQuotations}
        onRetry={fetchList}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(p) => setPageConfig((c) => ({ ...c, page: p }))}
        onRowsPerPageChange={(l) =>
          setPageConfig((c) => ({ ...c, limit: l, page: 1 }))
        }
        enableColumnManagement={true}
        rowAction={(row: QuotationSummaryDto) => (
          <Stack
            sx={{
              flexDirection: "row",
              spacing: 1,
              justifyContent: "center",
              gap: 1,
            }}
          >
            <Tooltip title="View Details">
              <IconButton
                size="small"
                color="primary"
                onClick={() => navigateToTab(`/quotations/${row.id}`)}
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {row.status !== "CONVERTED" && (
              <>
                <Tooltip title="Update Status">
                  <IconButton
                    color="secondary"
                    size="small"
                    onClick={(e) => handleMenuOpen(e, row.id)}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Quote">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleOpenDelete(row)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        )}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", px: 2, pb: 1, display: "block" }}
        >
          Update Status
        </Typography>
        {actionStatuses.map((st) => (
          <MenuItem key={st} onClick={() => handleStatusChange(st)}>
            {st}
          </MenuItem>
        ))}
      </Menu>

      {isCreateOpen && (
        <CreateQuotationModal
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          error={errorCreateQuote}
        />
      )}

      {isDeleteOpen && quoteForOperation && (
        <ConfirmDialog
          open={isDeleteOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Quotation"
          description={`Are you sure you want to delete quotation "${quoteForOperation.quoteNumber}"? This action cannot be undone.`}
          confirmText="Delete"
          severity="error"
          loading={loadingDeleteQuotation}
        />
      )}
    </PageContainer>
  );
};
export default QuotationsPage;
